/**
 * Localization.
 *
 * i18next + `i18next-icu` (intl-messageformat) rather than a lighter
 * alternative, for two reasons that come straight from the design doc:
 *
 *  - §5.6 requires ICU messages and forbids concatenating translated
 *    fragments. `i18next-icu` gives real ICU `plural`/`select`/`number`/`date`
 *    handling, so "3 brands are affected" and "1 brand is affected" are one
 *    message, not two strings glued together.
 *  - Locale is stored **per content variant**, not only per brand. i18next's
 *    `getFixedT(locale)` yields a translator bound to an explicit locale that
 *    is independent of the UI language, which is exactly what rendering a
 *    `zh-CN` content variant inside an `en-CA` chrome requires. Nothing in this
 *    module reads an ambient "current locale" without being asked to.
 */
import i18next, { type i18n as I18nInstance, type TFunction } from 'i18next';
import ICU from 'i18next-icu';
import { initReactI18next } from 'react-i18next';
import enCA from './locales/en-CA.json';
import zhCN from './locales/zh-CN.json';
import { env } from '@/lib/env';

export const SUPPORTED_LOCALES = ['en-CA', 'zh-CN'] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = 'en-CA';

export function isSupportedLocale(value: string): value is SupportedLocale {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * Best supported locale for a BCP-47 tag, matching on the primary subtag.
 *
 * Chinese content is independently adapted, not machine-translated (§5.6), so
 * this only ever decides which *catalogue* to read — never whether a content
 * variant may be shown in place of another.
 */
export function resolveLocale(tag: string | null | undefined): SupportedLocale {
  if (!tag) return DEFAULT_LOCALE;
  if (isSupportedLocale(tag)) return tag;
  const primary = tag.split('-')[0]?.toLowerCase();
  const match = SUPPORTED_LOCALES.find((l) => l.split('-')[0]?.toLowerCase() === primary);
  return match ?? DEFAULT_LOCALE;
}

/**
 * The interface languages offered, given the `chinese_ui` flag.
 *
 * Only the *interface* language: a zh-CN content variant is data and renders
 * through `translatorFor` whatever this returns.
 */
export function enabledLocales(chineseUi: boolean = env.chineseUi): readonly SupportedLocale[] {
  return chineseUi ? SUPPORTED_LOCALES : [DEFAULT_LOCALE];
}

const LOCALE_STORAGE_KEY = 'gp.locale';

/** The viewer's saved interface language, if it is still offered. */
export function readStoredLocale(chineseUi: boolean = env.chineseUi): SupportedLocale {
  try {
    const stored = resolveLocale(globalThis.localStorage?.getItem(LOCALE_STORAGE_KEY));
    return enabledLocales(chineseUi).includes(stored) ? stored : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

export function storeLocale(locale: SupportedLocale): void {
  try {
    globalThis.localStorage?.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    /* private-mode storage refusal must not break the UI */
  }
}

/** Creates a fully configured, isolated i18next instance. Tests get their own. */
export function createI18n(locale: SupportedLocale = DEFAULT_LOCALE): I18nInstance {
  const instance = i18next.createInstance();
  void instance
    .use(ICU)
    .use(initReactI18next)
    .init({
      lng: locale,
      fallbackLng: DEFAULT_LOCALE,
      supportedLngs: [...SUPPORTED_LOCALES],
      defaultNS: 'translation',
      resources: {
        'en-CA': { translation: enCA },
        'zh-CN': { translation: zhCN },
      },
      // ICU owns every substitution; i18next's own interpolation would be a
      // second, subtly different syntax for the same job.
      interpolation: { escapeValue: false },
      returnNull: false,
    });
  return instance;
}

export const i18n = createI18n(readStoredLocale());

/**
 * A translator bound to an explicit locale.
 *
 * Use this — never the ambient `t` — when rendering anything whose locale is a
 * property of the data (a content variant, a fact's `allowed_locales`) rather
 * than a property of the viewer.
 */
export function translatorFor(locale: string, instance: I18nInstance = i18n): TFunction {
  return instance.getFixedT(resolveLocale(locale));
}

export async function changeLocale(
  locale: SupportedLocale,
  instance: I18nInstance = i18n,
): Promise<void> {
  await instance.changeLanguage(locale);
  storeLocale(locale);
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}
